import { Button, TextField } from '@cimpress/react-components';
import IconRemove from '@cimpress-technology/react-streamline-icons/lib/IconRemove';
import { useState } from 'react';
import { isStringEmptyOrWhitespace, useUpdateEffect } from '../../../tools';
import { Control } from '../../layout';
import type { TextAsset } from './types';

export interface TextAssetEditorProps {
    assetId: string;
    initialValue?: TextAsset;
    onChange: (assetId: string, value: TextAsset) => void;
    onRemove: (assetId: string) => void;
}

interface TextState {
    content: string;
    context: string;
}

function assetToState(textAsset?: TextAsset): TextState {
    if (!textAsset) {
        return {
            content: '',
            context: '',
        };
    }

    const { content, context: context } = textAsset;

    return {
        content: content ?? '',
        context: context ?? '',
    };
}

export const TextAssetEditor = ({ assetId, initialValue, onChange, onRemove }: TextAssetEditorProps) => {
    const [text, setText] = useState(() => assetToState(initialValue));
    const [isContentDirty, setIsContentDirty] = useState(false);

    const { content, context: context } = text;

    useUpdateEffect(() => {
        if (!isStringEmptyOrWhitespace(content)) {
            onChange(assetId, {
                content,
                context: context,
            });
        }
    }, [assetId, content, context, onChange]);

    return (
        <Control spacing="compact">
            <TextField
                label="Content"
                required
                value={content}
                status={isContentDirty && isStringEmptyOrWhitespace(content) ? 'error' : undefined}
                onChange={(e) => setText((prev) => ({ ...prev, content: e.target.value }))}
                onInput={() => setIsContentDirty(true)}
                onBlur={() => setIsContentDirty(true)}
                rightAddon={
                    <Button
                        color="danger"
                        variant="primary"
                        icon={<IconRemove className="icon-addon" />}
                        aria-label="Remove text asset"
                        onClick={() => onRemove(assetId)}
                    />
                }
            />

            <TextField
                label="Context"
                required={false}
                value={context}
                onChange={(e) => setText((prev) => ({ ...prev, context: e.target.value }))}
            />
        </Control>
    );
};
